// @givmo/assistant-panel — the seam a page uses to draw the panel's body itself (`AssistantScope.pageBody`).
//
// The panel keeps its frame — its title, its close, where it sits — and on a `pageBody` scope it draws an
// empty body element and publishes it here. The page renders `<AssistantPageBody>` anywhere in its own
// tree, and its children are portalled into that element: they keep the PAGE's React context (its module
// provider, its queries, the hand editor's save it waits on), so moving a conversation into the panel
// moves no logic out of the page that owns it. While the panel is closed there is no element and the
// children are not drawn; whatever state the page holds (its thread, its draft) stays with the page.

import { useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

let target: HTMLElement | null = null;
const subscribers = new Set<() => void>();

/** The panel publishes its body element (or `null` when it closes or draws its own body). */
export function setAssistantPageBodyTarget(next: HTMLElement | null): void {
  if (target === next) return;
  target = next;
  for (const notify of subscribers) notify();
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

function getSnapshot(): HTMLElement | null {
  return target;
}

/** The panel's body element while a `pageBody` panel is open, else `null`. */
export function useAssistantPageBodyTarget(): HTMLElement | null {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

/** Draw `children` as the open panel's body. Renders nothing while the panel is closed. */
export function AssistantPageBody({ children }: { children: ReactNode }) {
  const element = useAssistantPageBodyTarget();
  return element === null ? null : createPortal(children, element);
}

// ══ A HAND EDIT TAKES THE ROOM BACK ══════════════════════════════════════════════════════════════
//
// Below `lg` the panel is a sheet OVER the page, and a page whose person starts editing by hand (the
// email designer's words, its subject line) needs the room the sheet covers: its Done, its Apply, the
// words themselves. The page calls `yieldAssistantSheet()` as an edit opens; a console's control closes
// its panel if, and only if, it is the sheet. Docked beside the page it covers nothing and stays open.

const yieldListeners = new Set<() => void>();

/** The page needs the room an open assistant SHEET covers (a hand edit is opening). Docked: no-op. */
export function yieldAssistantSheet(): void {
  for (const listener of yieldListeners) listener();
}

/** Hear a page's `yieldAssistantSheet()`. Returns the unsubscribe. */
export function subscribeAssistantSheetYield(listener: () => void): () => void {
  yieldListeners.add(listener);
  return () => {
    yieldListeners.delete(listener);
  };
}
